// utils/launchTarget.js
//
// Shared helpers for the Telegram → system-browser launch handoff.
// Used by both the bot-deep-link landing page (TgLaunchPage) and the in-app
// quick-launch FAB (OpenInBrowserFab) so the URL shape and platform-detection
// logic stay in lockstep.

import { adapter } from '@/platform/index.js';
import { issue } from '@/services/launchToken.service';

/**
 * Resolve the canonical browser URL for the launch landing page.
 *
 * We use the runtime origin so the URL always points at the same host the SPA
 * itself is served from — works for tunnel (mac.poolit.club), prod
 * (app.poolit.club), and preview deploys without env-file coordination.
 * Env-var fallbacks exist for unusual embedded contexts where `window` is
 * unavailable; production code should never hit them.
 *
 * @param {string} token - Single-use launch token from /auth/launch-token/issue.
 * @returns {string} Absolute https URL.
 */
export function buildLaunchUrl(token) {
  const base = (typeof window !== 'undefined' && window.location?.origin)
    || import.meta.env.VITE_APP_URL
    || 'https://app.poolit.club';
  return `${base.replace(/\/$/, '')}/launch?t=${encodeURIComponent(token)}`;
}

/**
 * Build an Android Chrome `intent://` URL that prefers Chrome and falls back
 * to the system default browser if Chrome is absent.
 *
 * Reference: https://developer.chrome.com/docs/multidevice/android/intents/
 *
 * @param {string} token
 * @returns {string} intent:// URL safe to hand to TmaAdapter.openExternalLink.
 */
export function buildChromeIntentUrl(token) {
  const target = buildLaunchUrl(token);
  const hostAndPath = target.replace(/^https?:\/\//, '');
  return `intent://${hostAndPath}`
    + '#Intent;scheme=https;package=com.android.chrome;'
    + `S.browser_fallback_url=${encodeURIComponent(target)};end`;
}

/**
 * Cheap, sufficient Android detection. We don't need fingerprint-grade
 * precision — the only consumer is the "Open in Chrome" affordance, and the
 * intent URL's `browser_fallback_url` covers the false-negative case.
 */
export function isAndroid() {
  if (typeof navigator === 'undefined') return false;
  const uad = navigator.userAgentData;
  if (uad?.platform) return /android/i.test(uad.platform);
  return /Android/i.test(navigator.userAgent || '');
}

/**
 * One-shot: issue a launch token and open the external link in one call.
 *
 * Throws the underlying error from `issue()` if token issuance fails;
 * `openExternalLink` itself does not throw (it falls back to window.open).
 *
 * @param {{ chrome?: boolean }} [options]
 * @returns {Promise<string>} The URL handed to the system browser.
 */
export async function launchInBrowser({ chrome = false } = {}) {
  const { token } = await issue();
  const url = chrome ? buildChromeIntentUrl(token) : buildLaunchUrl(token);
  adapter.openExternalLink(url);
  return url;
}

/**
 * The way back into Telegram when the browser has no session: the bot's
 * `tg://resolve` link. ONE implementation for LaunchPage's «افتح تيليجرام مرة
 * أخرى» and the collector's «افتح يومك من تيليجرام» card (collector-app P2-B,
 * L-D), so both take the same path.
 *
 * @param {{ fallback?: () => void }} [options] - run when no bot is configured
 * @returns {boolean} true when the Telegram link was opened
 */
export function openTelegramBot({ fallback } = {}) {
  const bot = import.meta.env.VITE_TELEGRAM_BOT_USERNAME;
  if (bot) {
    window.location.href = `tg://resolve?domain=${bot}`;
    return true;
  }
  fallback?.();
  return false;
}
